import { useTranslation } from "react-i18next";
import { Fragment, useCallback, useEffect, useState } from "react";
import { EmptyAvaibookIntegration, IntegrationCompany, integrationsByCompany, LocalEditableIntegration } from "../../../Everou/_modelsTS/Integration";
import { CapsuleButton, MainPageWithButtons } from "../../../Everou/_components";
import { removeFromArr } from "../../../Common/_utils/utils";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Selectors } from "../../../Everou/_reducers/app.reducer";
import { PMSIntegrationsActions } from "../../_actions/PMSIntegrationsActions";
import { PMSAvaibookNewIntegrationInput } from "../_components/PMSAvaibookNewIntegrationInput";
import { PMSAvaibookIntegrationInput } from "../_components/PMSAvaibookIntegrationInput";
import { AddIntegrationButton } from "../_components/AddIntegrationButton";
import { useAccountDraft } from "../_hooks/useAccountDraft";

export function PMSAvaibookNewIntegration() {

    const { t } = useTranslation();
    const dispatch = useDispatch();
    
    const serverIntegrations = useSelector(Selectors.getPMSIntegrationsInPanel);
    const accountDraft = useAccountDraft(IntegrationCompany.AVAIBOOK);
    const serverEditableIntegrations = serverIntegrations.map(integration => ({ ...integration, editable: false }))

    const [integrationsLocal, setIntegrationsLocal ] = useState<LocalEditableIntegration[]>(
        () => integrationsByCompany(serverIntegrations, IntegrationCompany.AVAIBOOK, accountDraft),
    );

    const locationUid = useSelector(Selectors.getSelectedLocationUid) ?? '';

    useEffect(() => {
        const updatedIntegrations = integrationsByCompany(serverIntegrations, IntegrationCompany.AVAIBOOK, accountDraft);
        setIntegrationsLocal(updatedIntegrations);
    }, [serverIntegrations, accountDraft])

    const canAddOnMore = useMemo(() => integrationsLocal.length === 0 || !!integrationsLocal[integrationsLocal.length - 1].uid
    , [integrationsLocal])

    const addIntegration = useCallback(() => {
        if(canAddOnMore) {
            setIntegrationsLocal([...integrationsLocal, EmptyAvaibookIntegration])
        }
    }, [canAddOnMore, integrationsLocal]);

    const onDeleteIntegration = (index: number) => {
        const integration = integrationsLocal[index];
        if(!integration.uid) {
            setIntegrationsLocal(removeFromArr<LocalEditableIntegration>(index, integrationsLocal))
        } else {
            dispatch(PMSIntegrationsActions.deleteIntegration(integration.uid))
            setIntegrationsLocal(removeFromArr<LocalEditableIntegration>(index, integrationsLocal))
        }
    }

    const toggleEditable = (index: number) => {
        const updatedIntegrations = integrationsLocal.map((integration, maxIndex) => index === maxIndex ? ({ ...integration, editable: !integration.editable}) : integration);
        setIntegrationsLocal(updatedIntegrations);
    }

    const onSetInitialStatus = (index: number) => {
        const updatedIntegrations = integrationsLocal.map((integration, maxIndex) => index === maxIndex ? serverEditableIntegrations[index] : integration)
        setIntegrationsLocal(updatedIntegrations)
    }

    const body = (
        <div className="c-grid c-grid--small rounded-bottom">
            {integrationsLocal.map((integration, index) => (
                // The legacy iCal input only for SAVED legacy rows: a draft row
                // (no uid yet) may carry the orphan's link and is still an account.
                !integration.link || !integration.uid ?
                <Fragment key={integration.uid || "new"}>
                    {!integration.uid && !!integration.link && !integration.apikey &&
                        // New Avaibook accounts connect with the API token
                        // only: the orphan's iCal link does not carry over,
                        // say so instead of dropping it silently.
                        <div className="col-span-full alert alert-info small mb-0">
                            {t("mod_integrations_avaibook_ical_draft_info")}
                        </div>
                    }
                    <PMSAvaibookNewIntegrationInput
                        onDelete={index => onDeleteIntegration(index)}
                        index={index}
                        onToggleEditable={index => toggleEditable(index)}
                        integration={integration}
                    />
                </Fragment>
                :
                <PMSAvaibookIntegrationInput
                    key={integration.uid || "new"}
                    onSetInitialStatus={(index) => onSetInitialStatus(index)}
                    onDelete={(index) => onDeleteIntegration(index)}
                    index={index}
                    onToggleEditable={(index) => toggleEditable(index)}
                    integration={integration}
                />
            ))}
            
        </div>
    );

    return (
        <MainPageWithButtons
            //dataTestId={testIDs.root}
            pageTitle={t("mod_integrations_avaibook_title")}
            content={body}
            buttons={[
                <AddIntegrationButton integrations={integrationsLocal} isEnabled={canAddOnMore} onClick={addIntegration} />,
            ]}
        />
    );
}

